// @ts-nocheck
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import OriginalFieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps, ButtonVariants } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Hidden from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { StyledImgWrapper } from "@store/MobiusOverrides/Img/Img";
import { TypographyProps } from "@store/MobiusOverrides/Typography/Typography";
import { headerHideForDesktopStyles, headerHideForMobileStyles } from "@store/Themes/common/mixins/header";
import { borderedTitle } from "@store/Themes/common/mixins/title";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const MAX_WIDTH = 1530;
const MAX_WIDTH_MOBILE = 420;

const enum fields {
    customCSS = "customCSS",
    clickableSlide = "clickableSlide",
    background = "background",
    backgroundImage = "backgroundImage",
    backgroundImageLargeResolution = "backgroundImageLargeResolution",
    image = "image",
    imageLargeResolution = "imageLargeResolution",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    backgroundColor = "backgroundColor",
    heading = "heading",
    headingUseRichText = "headingUseRichText",
    headingRichText = "headingRichText",
    subheading = "subheading",
    text = "text",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "buttonVariant",
    focalPoint = "focalPoint",
    contentPadding = "contentPadding",
    contentMaxHeight = "contentMaxHeight",
    headingStyle = "headingStyle",
    headingColor = "headingColor",
    headingUnderline = "headingUnderline",
    subheadingColor = "subheadingColor",
    textColor = "textColor",
    verticalAlign = "verticalAlign",
    textAlign = "textAlign",
    contentSmallDesktopWidth = "contentSmallDesktopWidth",
    contentDesktopWidth = "contentDesktopWidth",
    hasContentMobileWidth = "hasContentMobileWidth",
    contentMobileWidth = "contentMobileWidth",
    hasContentTabletWidth = "hasContentTabletWidth",
    contentTabletWidth = "contentTabletWidth",
    contentPosition = "contentPosition",
    contentPositionLeftRight = "contentPositionLeftRight",
    mobileVerticalAlign = "mobileVerticalAlign",
}

export interface SlideOwnProps extends WidgetProps, ExpandStyles<SlideStyles> {
    fields: {
        [fields.customCSS]: string;
        [fields.clickableSlide]: boolean;
        [fields.background]: "image" | "color";
        [fields.backgroundImage]: string;
        [fields.backgroundImageLargeResolution]: string;
        [fields.image]: string;
        [fields.imageLargeResolution]: string;
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.headingUseRichText]: boolean;
        [fields.headingRichText]: string;
        [fields.subheading]: string;
        [fields.text]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary";
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.contentPadding]: number;
        [fields.contentMaxHeight]: number;
        [fields.headingStyle]: string;
        [fields.headingColor]: string;
        [fields.headingUnderline]: boolean;
        [fields.subheadingColor]: string;
        [fields.textColor]: string;
        [fields.verticalAlign]: "top" | "center" | "bottom" | "spaceBetween";
        [fields.textAlign]: "left" | "center" | "right";
        [fields.contentSmallDesktopWidth]: string;
        [fields.contentDesktopWidth]: string;
        [fields.hasContentMobileWidth]: boolean;
        [fields.contentMobileWidth]: string;
        [fields.hasContentTabletWidth]: boolean;
        [fields.contentTabletWidth]: string;
        [fields.contentPosition]: "left" | "center" | "right";
        [fields.contentPositionLeftRight]: "left" | "right" | "";
        [fields.mobileVerticalAlign]: "top" | "center" | "bottom" | "";
    };
    mobileContentOverlay: boolean;
}

export interface SlideStyles {
    slideWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    slideCenteringWrapperStylesInOverlay?: InjectableCss;
    headingText?: TypographyPresentationProps;
    headingRichText?: InjectableCss;
    subheadingText?: TypographyPresentationProps;
    text?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    slideImageWrapperStyles?: InjectableCss;
    image?: ImgProps;
}

export const slideStyles: SlideStyles = {
    slideWrapper: {
        css: css`
            height: 100%;
            flex-direction: column;
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            flex: 0 0 100%;
            position: relative;
            background-repeat: no-repeat;
            background-size: cover;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: row;
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            position: relative;
            height: 100%;
            display: flex;
            flex-direction: column;
            ${breakpointValues([1, "inherit", null, null, null], "order")};
        `,
    },
    slideImageWrapperStyles: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            ${breakpointValues([0, "inherit", null, null, null], "order")};
        `,
    },
    headingText: {
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.smalll};
            display: block;
        `,
    },
    headingRichText: {
        css: css`
            display: block;
            margin-bottom: 0.5rem;

            h1 {
                margin-top: 0;
            }
        `,
    },
    subheadingText: {
        css: css`
            line-height: ${x => x.theme.design.typography.lineHeight.smalll};
            display: block;
            margin-bottom: 0.5rem;
        `,
    },
    text: {
        css: css`
            display: block;
        `,
    },
    slideButton: {
        css: css`
            ${spacingStyles(pxToRem(16))}
        `,
    },
    image: {
        css: css`
            ${breakpointValues(["240px", null, "100%", null, null], "max-width")};
            width: 100%;
            height: auto;

            &${StyledImgWrapper} {
                max-width: 100%;
            }

            img {
                max-width: 100%;
            }
        `,
    },
    slideCenteringWrapperStylesInOverlay: {
        css: css`
            background-color: ${getColor("primary")};
            color: white !important;
        `,
    },
};

const defaultCustomCss = `.slide-wrapper {
}

.slide-heading {
}

.slide-subheading {
}

.slide-button {
}
`;

export const Slide = ({ id, fields, extendedStyles, overridedStyles, mobileContentOverlay }: SlideOwnProps) => {
    const { isInShell } = useShellContext();
    const history = useHistory();
    const buttonLink = useGetLink(fields.buttonLink);
    const styles = useExpandProps(slideStyles, extendedStyles, overridedStyles);

    const onClick = (url: string | undefined) => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    if (!fields.heading && !fields.subheading && !fields.text) {
        return null;
    }

    const bg = fields.backgroundImage;
    const bgHiRes = fields.backgroundImageLargeResolution;

    const hasImage = (imgField: string) => {
        return !!(imgField && imgField !== "");
    };

    const backgroundStyles =
        fields.background === "image"
            ? css`
                  ${breakpointValues([`url(${bg || bgHiRes})`, `url(${bgHiRes || bg})`], "background-image")}
                  background-position: center;
              `
            : `background-color: ${fields.backgroundColor};`;
    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = "align-items: flex-end;";
    }

    let textAlign;
    let borderedTitleAlignement;
    let horizontalAlignMargins;
    switch (fields.textAlign) {
        case "left":
            textAlign = "flex-start";
            borderedTitleAlignement = "";
            break;
        case "center":
            textAlign = "center";
            borderedTitleAlignement = `
                left: 50%;
                transform: translateX(-50%);
            `;
            break;
        case "right":
            textAlign = "flex-end";
            borderedTitleAlignement = `
                left: inherit;
                right: 0;
            `;
            break;
    }
    const contentPosition =
        fields.contentPositionLeftRight !== undefined && fields.contentPositionLeftRight !== ""
            ? fields.contentPositionLeftRight
            : fields.contentPosition;
    switch (contentPosition) {
        case "left":
            horizontalAlignMargins = `
                margin-right: auto;
                margin-left: 0;
            `;
            break;
        case "center":
            horizontalAlignMargins = `
                margin-right: auto;
                margin-left: auto;
            `;
            break;
        case "right":
            horizontalAlignMargins = `
                margin-right: 0;
                margin-left: auto;
            `;
            break;
    }
    let verticalAlign;
    switch (fields.verticalAlign) {
        case "top":
            verticalAlign = "flex-start";
            break;
        case "center":
            verticalAlign = "center";
            break;
        case "bottom":
            verticalAlign = "flex-end";
            break;
        case "spaceBetween":
            verticalAlign = "space-between";
            break;
    }
    let mobileVerticalAlignValue;
    switch (fields.mobileVerticalAlign) {
        case "top":
            mobileVerticalAlignValue = "flex-start";
            break;
        case "center":
            mobileVerticalAlignValue = "center";
            break;
        case "bottom":
            mobileVerticalAlignValue = "flex-end";
            break;
        case "":
            mobileVerticalAlignValue = verticalAlign;
            break;
    }

    let buttonVariantDefaultStyles;
    switch (fields.buttonVariant) {
        case "primary":
            buttonVariantDefaultStyles = css`
                ${x => x.theme.button.primary?.css}
            `;
            break;
        case "secondary":
            buttonVariantDefaultStyles = css`
                ${x => x.theme.button.secondary?.css}
            `;
            break;
        case "tertiary":
            buttonVariantDefaultStyles = css`
                ${x => x.theme.button.tertiary?.css}
            `;
            break;
    }

    const contentMobileWidth =
        hasImage(fields.image) || hasImage(fields.imageLargeResolution)
            ? "100%"
            : fields.hasContentMobileWidth
            ? fields.contentMobileWidth
            : "100%";
    const contentTabletWidth =
        hasImage(fields.image) || hasImage(fields.imageLargeResolution)
            ? "50%"
            : fields.hasContentTabletWidth
            ? fields.contentTabletWidth
            : "100%";
    const contentSmallDesktopWidth =
        hasImage(fields.image) || hasImage(fields.imageLargeResolution) ? "50%" : fields.contentSmallDesktopWidth;
    const contentDesktopWidth =
        hasImage(fields.image) || hasImage(fields.imageLargeResolution) ? "50%" : fields.contentDesktopWidth;

    const slideContentWrapperStyles = {
        css: css`
            ${styles.slideContentWrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${overlayPositioningStyles};
            ${!mobileContentOverlay ? "height: 100%;" : null}
        `,
    };

    const slideOverlayWrapperStyles = {
        css: css`
            ${styles.slideOverlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            ${breakpointValues(
                hasImage(fields.image) || hasImage(fields.imageLargeResolution)
                    ? ["column", "row", null, null, null]
                    : ["column", null, "row", null, null],
                "flex-direction",
            )};
        `,
    };

    const hasImages = hasImage(fields.image) || hasImage(fields.imageLargeResolution);
    const slideCenteringWrapperStyles = {
        css: css`
            ${styles.slideCenteringWrapperStyles?.css}
            ${horizontalAlignMargins}
            ${breakpointValues(
                [contentMobileWidth, contentTabletWidth, contentSmallDesktopWidth, contentDesktopWidth, null],
                "width",
            )}
            ${breakpointValues(
                [pxToRem(fields.contentPadding * 0.85), null, pxToRem(fields.contentPadding)],
                "padding",
            )}
            ${hasImages
                ? css`
                      ${breakpoint("sm", "up")} {
                          padding-${contentPosition === "left" ? "right" : "left"}: ${pxToRem(
                      fields.contentPadding * 0.5,
                  )};
                      }
                  `
                : css`
                      ${breakpoint("sm", "down")} {
                          padding-top: ${pxToRem(fields.contentPadding * 2)};
                      }
                  `}
            text-align: ${fields.textAlign};
            align-items: ${textAlign};
            ${breakpoint("xs", "down")} {
                text-align: left;
                align-items: flex-start;
            }
            ${fields.contentMaxHeight
                ? css`
                      ${breakpointValues([null, pxToRem(fields.contentMaxHeight)], "max-height")}
                      ${breakpointValues([null, `${(fields.contentMaxHeight / MAX_WIDTH) * 100}vw`], "height")}
                  `
                : null}
            ${mobileContentOverlay
                ? css`
                      ${breakpoint("xs", "down")} {
                          aspect-ratio: 1;
                      }
                  `
                : null}
            ${breakpointValues([mobileVerticalAlignValue, verticalAlign], "justify-content")};
        `,
    };

    const slideCenteringWrapperStylesInOverlay = mobileContentOverlay
        ? {
              css: css`
                  ${styles.slideCenteringWrapperStylesInOverlay?.css}
                  ${horizontalAlignMargins}
                  align-items: ${textAlign};
                  justify-content: ${mobileVerticalAlignValue};
                  width: 100%;
                  padding: ${pxToRem(fields.contentPadding * 0.85)};
                  ${breakpoint("xs", "down")} {
                      text-align: left;
                  }
                  ${breakpoint("sm", "down")} {
                      height: 100%;
                  }
              `,
          }
        : null;

    const slideImageWrapperStyles = {
        css: css`
            ${styles.slideImageWrapperStyles?.css}
            ${breakpointValues(
                [`100%`, `calc(100% - ${contentTabletWidth})`, null, `calc(100% - ${contentDesktopWidth})`],
                "width",
            )}
            ${breakpointValues(
                [
                    `${pxToRem(fields.contentPadding)} ${pxToRem(fields.contentPadding)} 0`,
                    `${
                        fields.contentPosition === "left" || fields.contentPositionLeftRight === "left"
                            ? `${pxToRem(fields.contentPadding)} ${pxToRem(fields.contentPadding)} ${pxToRem(
                                  fields.contentPadding,
                              )} 0`
                            : `${pxToRem(fields.contentPadding)} 0 ${pxToRem(fields.contentPadding)} ${pxToRem(
                                  fields.contentPadding,
                              )}`
                    }`,
                    null,
                ],
                "padding",
            )}
        `,
    };

    const titleStyles = {
        variant: fields.headingStyle as any,
        forwardAs: "p" as any,
        color: fields.headingColor as string,
        css: css`
            ${styles.headingText?.css}
            margin-bottom: ${pxToRem(8)};

            ${fields.headingUnderline ? borderedTitle(null, null, 70) : ""}
            ${fields.headingUnderline &&
            `
                &::after {
                    ${borderedTitleAlignement}
                }
            `}
        `,
    };

    const titleRichTextStyles = {
        color: fields.headingColor as string,
        css: css`
            ${styles.headingRichText?.css}
        `,
    };

    const subHeadingStyles = {
        color: fields.subheadingColor as any,
        css: css`
            ${styles.subheadingText?.css}
        `,
    };

    const textStyles = {
        color: fields.textColor as any,
        css: css`
            ${styles.text?.css}
        `,
    };
    const bannerButtonStyles = {
        variant: fields.buttonVariant,
        sizeVariant: "small" as any,
        css: css`
            ${styles.slideButton?.css}
            ${buttonVariantDefaultStyles}
        `,
    };
    const bannerButtonStylesInOverlay = {
        variant: "secondary" as ButtonVariants,
        sizeVariant: "small" as any,
        css: css`
            ${styles.slideButton?.css}
            ${x => x.theme.button.secondary?.css}
        `,
    };

    const clickableStyles = {
        css: css`
            height: 100%;
            flex: 0 0 100%;
        `,
    };

    const imageCss = React.useMemo(
        () => css`
            ${hasImage(fields.image) && hasImage(fields.imageLargeResolution) ? headerHideForDesktopStyles() : null}
            ${styles.image?.css}
        `,
        [styles, fields.image, fields.imageLargeResolution],
    );
    const imageLargeResolutionCss = React.useMemo(
        () => css`
            ${hasImage(fields.image) && hasImage(fields.imageLargeResolution) ? headerHideForMobileStyles() : null}
            ${styles.image?.css}
        `,
        [styles, fields.image, fields.imageLargeResolution],
    );

    const img = fields.image;
    const imgHiRes = fields.imageLargeResolution;

    const RenderContent = (bp?: string) => (
        <StyledWrapper
            {...(bp === "desktop" || bp === undefined
                ? slideCenteringWrapperStyles
                : slideCenteringWrapperStylesInOverlay)}
        >
            <div>
                {(bp === "desktop" || bp === undefined) && fields.heading && (
                    <>
                        {fields.headingUseRichText ? (
                            <VisuallyHidden>
                                <Typography {...titleStyles} className="slide-heading">
                                    {fields.heading}
                                </Typography>
                            </VisuallyHidden>
                        ) : (
                            <Typography {...titleStyles} forwardAs="h1" className="slide-heading">
                                {fields.heading}
                            </Typography>
                        )}
                    </>
                )}
                {(bp === "desktop" || bp === undefined) && fields.headingUseRichText && (
                    <Typography {...titleRichTextStyles} className="slide-heading" aria-hidden="true">
                        {parse(fields.headingRichText, parserOptions)}
                    </Typography>
                )}
                {(bp === "desktop" || bp === undefined) && fields.subheading && (
                    <Typography {...subHeadingStyles} className="slide-subheading" aria-hidden={bp === "desktop"}>
                        {parse(fields.subheading, parserOptions)}
                    </Typography>
                )}
                {(bp === "mobile" || bp === undefined) && fields.text && (
                    <Typography {...(bp === undefined ? textStyles : {})} className="slide-text">
                        {parse(fields.text, parserOptions)}
                    </Typography>
                )}
            </div>
            {(bp === "mobile" || bp === undefined) &&
                !fields.clickableSlide &&
                fields.buttonLabel &&
                fields.buttonLink?.value && (
                    <Button
                        {...(bp === "mobile" ? bannerButtonStylesInOverlay : bannerButtonStyles)}
                        className="slide-button"
                        onClick={() => onClick(buttonLink?.url)}
                    >
                        {fields.buttonLabel || buttonLink?.title || buttonLink?.url}
                    </Button>
                )}
        </StyledWrapper>
    );

    const renderImage = () =>
        (hasImage(fields.image) || hasImage(fields.imageLargeResolution)) && (
            <StyledWrapper {...slideImageWrapperStyles}>
                {hasImage(fields.image) && <Img css={imageCss} src={img} />}
                {hasImage(fields.imageLargeResolution) && <Img css={imageLargeResolutionCss} src={imgHiRes} />}
            </StyledWrapper>
        );

    const RenderSlide = () => (
        <>
            <StyledWrapper className="slide-wrapper" {...slideContentWrapperStyles}>
                <StyledWrapper {...slideOverlayWrapperStyles}>
                    {fields.contentPositionLeftRight !== "left" && renderImage()}

                    {mobileContentOverlay ? (
                        <>
                            <Hidden
                                above="sm"
                                css={css`
                                    width: 100%;
                                    order: 2;
                                `}
                            >
                                {RenderContent("desktop")}
                            </Hidden>
                            <Hidden
                                below="md"
                                css={css`
                                    width: 100%;
                                    order: 2;
                                `}
                            >
                                {RenderContent()}
                            </Hidden>
                        </>
                    ) : (
                        RenderContent()
                    )}

                    {fields.contentPositionLeftRight === "left" && renderImage()}
                </StyledWrapper>
            </StyledWrapper>
            {mobileContentOverlay && (
                <Hidden
                    above="sm"
                    css={css`
                        width: 100%;
                        height: 100%;
                        order: 2;
                    `}
                >
                    {RenderContent("mobile")}
                </Hidden>
            )}
        </>
    );

    return (
        <>
            {fields.clickableSlide && buttonLink?.url ? (
                <Clickable href={buttonLink?.url} {...clickableStyles}>
                    {RenderSlide()}
                </Clickable>
            ) : (
                RenderSlide()
            )}
        </>
    );
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const headingOptions = [
    { displayName: "Heading 1", value: "h1" },
    { displayName: "Heading 2", value: "h2" },
    { displayName: "Heading 3", value: "h3" },
    { displayName: "Heading 4", value: "h4" },
    { displayName: "Heading 5", value: "h5" },
    { displayName: "Heading 6", value: "h6" },
];
export type SlideFieldDefinitionModel = {
    customCSS?: boolean;
    clickableSlide?: boolean;
    background?: boolean;
    backgroundImage?: boolean;
    backgroundImageLargeResolution?: boolean;
    image?: boolean;
    imageLargeResolution?: boolean;
    imageOverlay?: boolean;
    partialOverlay?: boolean;
    partialOverlayPositioning?: boolean;
    backgroundColor?: boolean;
    heading?: boolean;
    headingUseRichText?: boolean;
    headingRichText?: boolean;
    subheading?: boolean;
    text?: boolean;
    buttonLabel?: boolean;
    buttonLink?: boolean;
    buttonVariant?: boolean;
    focalPoint?: boolean;
    contentPadding?: boolean;
    contentMaxHeight?: boolean;
    contentMaxHeightMobile?: boolean;
    headingStyle?: boolean;
    headingColor?: boolean;
    headingUnderline?: boolean;
    subheadingColor?: boolean;
    textColor?: boolean;
    verticalAlign?: boolean;
    mobileVerticalAlign?: boolean;
    textAlign?: boolean;
    contentSmallDesktopWidth?: boolean;
    contentDesktopWidth?: boolean;
    hasContentMobileWidth?: boolean;
    contentMobileWidth?: boolean;
    hasContentTabletWidth?: boolean;
    contentTabletWidth?: boolean;
    contentPosition?: boolean;
    contentPositionLeftRight?: boolean;
};

export const getSlideFieldDefinitions = (slideFieldDefinition: SlideFieldDefinitionModel) =>
    [
        ...(slideFieldDefinition.clickableSlide
            ? [
                  {
                      fieldType: "General",
                      name: fields.clickableSlide,
                      displayName: "Open Link on Slide Click",
                      editorTemplate: "CheckboxField",
                      variant: "toggle",
                      defaultValue: false,
                      tab: settingsTab,
                  },
              ]
            : []),
        ...(slideFieldDefinition.background
            ? [
                  {
                      fieldType: "General",
                      name: fields.background,
                      displayName: "Background",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Image", value: "image" },
                          { displayName: "Color", value: "color" },
                      ],
                      defaultValue: "image",
                      hideEmptyOption: true,
                  },
              ]
            : []),
        ...(slideFieldDefinition.backgroundImage
            ? [
                  {
                      fieldType: "General",
                      name: fields.backgroundImage,
                      tooltip: "Ideal width for mobile usage: 600px x 600px. Must be square.",
                      editorTemplate: "ImagePickerField",
                      defaultValue: "",
                      isVisible: (widget: HasFields) =>
                          widget.fields.background === "image" || !slideFieldDefinition.background,
                  },
              ]
            : []),
        ...(slideFieldDefinition.backgroundImageLargeResolution
            ? [
                  {
                      fieldType: "General",
                      name: fields.backgroundImageLargeResolution,
                      tooltip: "Ideal width for desktop full width usage: 1400px",
                      editorTemplate: "ImagePickerField",
                      defaultValue: "",
                      isVisible: (widget: HasFields) =>
                          widget.fields.background === "image" || !slideFieldDefinition.background,
                  },
              ]
            : []),
        ...(slideFieldDefinition.image
            ? [
                  {
                      fieldType: "General",
                      name: fields.image,
                      editorTemplate: "ImagePickerField",
                      defaultValue: "",
                  },
              ]
            : []),
        ...(slideFieldDefinition.imageLargeResolution
            ? [
                  {
                      fieldType: "General",
                      name: fields.imageLargeResolution,
                      tooltip: "Higher resolution image, use only if widget will be displayed full width.",
                      editorTemplate: "ImagePickerField",
                      defaultValue: "",
                  },
              ]
            : []),
        ...(slideFieldDefinition.focalPoint
            ? [
                  {
                      fieldType: "General",
                      name: fields.focalPoint,
                      displayName: "Focal Point",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Top Left", value: "topLeft" },
                          { displayName: "Top Center", value: "topCenter" },
                          { displayName: "Top Right", value: "topRight" },
                          { displayName: "Center Left", value: "centerLeft" },
                          { displayName: "Center", value: "center" },
                          { displayName: "Center Right", value: "centerRight" },
                          { displayName: "Bottom Left", value: "bottomLeft" },
                          { displayName: "Bottom Center", value: "bottomCenter" },
                          { displayName: "Bottom Right", value: "bottomRight" },
                      ],
                      defaultValue: "center",
                      hideEmptyOption: true,
                      isVisible: (widget: HasFields) =>
                          widget.fields.background === "image" || !slideFieldDefinition.background,
                  },
              ]
            : []),
        ...(slideFieldDefinition.imageOverlay
            ? [
                  {
                      fieldType: "General",
                      name: fields.imageOverlay,
                      displayName: "Image Color Overlay",
                      editorTemplate: "ColorPickerField",
                      defaultValue: "",
                      isVisible: (widget: HasFields) =>
                          widget.fields.background === "image" || !slideFieldDefinition.background,
                  },
              ]
            : []),
        ...(slideFieldDefinition.partialOverlay
            ? [
                  {
                      fieldType: "General",
                      name: fields.partialOverlay,
                      editorTemplate: "CheckboxField",
                      defaultValue: false,
                      isVisible: (widget: HasFields) =>
                          widget.fields.background === "image" || !slideFieldDefinition.background,
                  },
              ]
            : []),
        ...(slideFieldDefinition.partialOverlayPositioning
            ? [
                  {
                      fieldType: "General",
                      name: fields.partialOverlayPositioning,
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Top", value: "top" },
                          { displayName: "Middle", value: "middle" },
                          { displayName: "Bottom", value: "bottom" },
                      ],
                      hideEmptyOption: true,
                      defaultValue: "bottom",
                      isVisible: (widget: HasFields) => widget.fields.partialOverlay,
                  },
              ]
            : []),
        ...(slideFieldDefinition.backgroundColor
            ? [
                  {
                      fieldType: "General",
                      name: fields.backgroundColor,
                      editorTemplate: "ColorPickerField",
                      displayName: "Color",
                      defaultValue: "black",
                      isVisible: (widget: HasFields) =>
                          widget.fields.background === "color" || !slideFieldDefinition.background,
                  },
              ]
            : []),
        {
            fieldType: "General",
            name: "horizontalRule0",
            editorTemplate: "HorizontalRule",
            defaultValue: "",
        },
        ...(slideFieldDefinition.heading
            ? [
                  {
                      fieldType: "General",
                      name: fields.heading,
                      editorTemplate: "TextField",
                      displayName: "Title",
                      defaultValue: "",
                      isRequired: true,
                  },
              ]
            : []),
        ...(slideFieldDefinition.headingUseRichText
            ? [
                  {
                      fieldType: "General",
                      name: fields.headingUseRichText,
                      editorTemplate: "CheckboxField",
                      displayName: "Use RichText for Title",
                      defaultValue: false,
                  },
              ]
            : []),
        ...(slideFieldDefinition.headingRichText
            ? [
                  {
                      fieldType: "General",
                      name: fields.headingRichText,
                      displayName: "Title Content",
                      tooltip:
                          "Use this to display a title with more styling option. Text title is still required and will be visually hidden but used for accessibility purpose.",
                      editorTemplate: "RichTextField",
                      defaultValue: "",
                      extendedConfig: { height: 170 },
                      isVisible: (widget: HasFields) => widget.fields.headingUseRichText === true,
                  },
              ]
            : []),
        ...(slideFieldDefinition.headingStyle
            ? [
                  {
                      fieldType: "General",
                      name: fields.headingStyle,
                      displayName: "Title Display Style",
                      editorTemplate: "DropDownField",
                      options: headingOptions,
                      defaultValue: "h2",
                      hideEmptyOption: true,
                  },
              ]
            : []),
        ...(slideFieldDefinition.headingColor
            ? [
                  {
                      fieldType: "General",
                      name: fields.headingColor,
                      displayName: "Title Color",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Red", value: "accent.main" },
                          { displayName: "White", value: "primary.contrast" },
                          { displayName: "Blue", value: "primary" },
                          { displayName: "Light Blue", value: "secondary" },
                          { displayName: "Black", value: "text.main" },
                      ],
                      defaultValue: "accent.main",
                      hideEmptyOption: true,
                  },
              ]
            : []),
        ...(slideFieldDefinition.headingUnderline
            ? [
                  {
                      fieldType: "General",
                      name: fields.headingUnderline,
                      displayName: "Red line under title?",
                      editorTemplate: "CheckboxField",
                      defaultValue: true,
                  },
              ]
            : []),
        ...(slideFieldDefinition.subheading
            ? [
                  {
                      fieldType: "General",
                      name: fields.subheading,
                      editorTemplate: "RichTextField",
                      displayName: "Sub Title",
                      defaultValue: "",
                  },
              ]
            : []),
        ...(slideFieldDefinition.subheadingColor
            ? [
                  {
                      fieldType: "General",
                      name: fields.subheadingColor,
                      displayName: "Sub Title Color",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Red", value: "accent.main" },
                          { displayName: "White", value: "primary.contrast" },
                          { displayName: "Blue", value: "primary" },
                          { displayName: "Light Blue", value: "secondary" },
                          { displayName: "Black", value: "text.main" },
                      ],
                      defaultValue: "text.main",
                      hideEmptyOption: true,
                  },
              ]
            : []),
        ...(slideFieldDefinition.text
            ? [
                  {
                      fieldType: "General",
                      name: fields.text,
                      displayName: "Text",
                      editorTemplate: "RichTextField",
                      defaultValue: "",
                      extendedConfig: { height: 170 },
                  },
              ]
            : []),
        ...(slideFieldDefinition.textColor
            ? [
                  {
                      fieldType: "General",
                      name: fields.textColor,
                      displayName: "Text Color",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "White", value: "primary.contrast" },
                          { displayName: "Black", value: "text.main" },
                      ],
                      defaultValue: "text.main",
                      hideEmptyOption: true,
                  },
              ]
            : []),
        {
            fieldType: "General",
            name: "horizontalRule1",
            editorTemplate: "HorizontalRule",
            defaultValue: "",
        },
        ...(slideFieldDefinition.buttonLabel
            ? [
                  {
                      fieldType: "General",
                      name: fields.buttonLabel,
                      editorTemplate: "TextField",
                      displayName: "Button Label",
                      defaultValue: "",
                  },
              ]
            : []),
        ...(slideFieldDefinition.buttonLink
            ? [
                  {
                      fieldType: "General",
                      name: fields.buttonLink,
                      displayName: "Button Link",
                      editorTemplate: "LinkField",
                      defaultValue: { type: "Page", value: "" },
                  },
              ]
            : []),
        ...(slideFieldDefinition.buttonVariant
            ? [
                  {
                      fieldType: "General",
                      name: fields.buttonVariant,
                      displayName: "Button Variant",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Primary", value: "primary" },
                          { displayName: "Secondary", value: "secondary" },
                          { displayName: "Tertiary", value: "tertiary" },
                      ],
                      hideEmptyOption: true,
                      defaultValue: "primary",
                  },
              ]
            : []),
        {
            fieldType: "General",
            name: "horizontalRule2",
            editorTemplate: "HorizontalRule",
            defaultValue: "",
        },
        ...(slideFieldDefinition.textAlign
            ? [
                  {
                      fieldType: "General",
                      name: fields.textAlign,
                      displayName: "Text Alignement",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Left", value: "left" },
                          { displayName: "Center", value: "center" },
                          { displayName: "Right", value: "right" },
                      ],
                      hideEmptyOption: true,
                      defaultValue: "left",
                  },
              ]
            : []),
        ...(slideFieldDefinition.contentPosition
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentPosition,
                      displayName: "Content Horizontal Position",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Left", value: "left" },
                          { displayName: "Center", value: "center" },
                          { displayName: "Right", value: "right" },
                      ],
                      hideEmptyOption: true,
                      defaultValue: "left",
                  },
              ]
            : []),
        ...(slideFieldDefinition.contentPositionLeftRight
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentPositionLeftRight,
                      displayName: "Content Horizontal Position",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "", value: "" },
                          { displayName: "Left", value: "left" },
                          { displayName: "Right", value: "right" },
                      ],
                      hideEmptyOption: true,
                      defaultValue: "",
                  },
              ]
            : []),
        ...(slideFieldDefinition.verticalAlign
            ? [
                  {
                      fieldType: "General",
                      name: fields.verticalAlign,
                      displayName: "Content Vertical Position",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Top", value: "top" },
                          { displayName: "Center", value: "center" },
                          { displayName: "Bottom", value: "bottom" },
                          { displayName: "Space-Between", value: "spaceBetween" },
                          { displayName: "Space-Around", value: "spaceAround" },
                      ],
                      hideEmptyOption: true,
                      defaultValue: "center",
                  },
              ]
            : []),
        ...(slideFieldDefinition.mobileVerticalAlign
            ? [
                  {
                      fieldType: "General",
                      name: fields.mobileVerticalAlign,
                      displayName: "Content Vertical Position Mobile",
                      editorTemplate: "DropDownField",
                      options: [
                          { displayName: "Top", value: "top" },
                          { displayName: "Center", value: "center" },
                          { displayName: "Bottom", value: "bottom" },
                          { displayName: "Same as non mobile", value: "" },
                      ],
                      hideEmptyOption: true,
                      defaultValue: "",
                  },
              ]
            : []),
        ...(slideFieldDefinition.contentPadding
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentPadding,
                      editorTemplate: "IntegerField",
                      defaultValue: 50,
                  },
              ]
            : []),
        ...(slideFieldDefinition.contentMaxHeight
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentMaxHeight,
                      tooltip: `Maximum height of the slide at full width resolution wich is ${MAX_WIDTH}px.`,
                      editorTemplate: "IntegerField",
                      defaultValue: 0,
                  },
              ]
            : []),
        {
            fieldType: "General",
            name: "horizontalRule3",
            editorTemplate: "HorizontalRule",
            defaultValue: "",
        },
        ...(slideFieldDefinition.contentDesktopWidth
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentDesktopWidth,
                      displayName: "Largest Desktop Content Width",
                      editorTemplate: "DropDownField",
                      hideEmptyOption: true,
                      defaultValue: "100%",
                      options: [
                          { value: "50%" },
                          { value: "66%" },
                          { value: "75%" },
                          { value: "80%" },
                          { value: "100%" },
                      ],
                  },
              ]
            : []),
        ...(slideFieldDefinition.contentSmallDesktopWidth
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentSmallDesktopWidth,
                      displayName: "Desktop Content Width",
                      editorTemplate: "DropDownField",
                      hideEmptyOption: true,
                      defaultValue: "100%",
                      options: [
                          { value: "50%" },
                          { value: "66%" },
                          { value: "75%" },
                          { value: "80%" },
                          { value: "100%" },
                      ],
                  },
              ]
            : []),
        ...(slideFieldDefinition.hasContentMobileWidth
            ? [
                  {
                      fieldType: "General",
                      name: fields.hasContentMobileWidth,
                      displayName: "Specific Mobile Content Width?",
                      editorTemplate: "CheckboxField",
                      defaultValue: false,
                      isVisible: (widget: HasFields) => widget.fields.image === undefined,
                  },
              ]
            : []),
        ...(slideFieldDefinition.contentMobileWidth
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentMobileWidth,
                      displayName: "Mobile Content Width",
                      editorTemplate: "DropDownField",
                      hideEmptyOption: true,
                      defaultValue: "50%",
                      options: [
                          { value: "50%" },
                          { value: "66%" },
                          { value: "75%" },
                          { value: "80%" },
                          { value: "100%" },
                      ],
                      isVisible: (widget: HasFields) => widget.fields.hasContentMobileWidth,
                  },
              ]
            : []),
        ...(slideFieldDefinition.hasContentTabletWidth
            ? [
                  {
                      fieldType: "General",
                      name: fields.hasContentTabletWidth,
                      displayName: "Specific Tablet Content Width?",
                      editorTemplate: "CheckboxField",
                      defaultValue: false,
                      variant: "toggle",
                      isVisible: (widget: HasFields) => widget.fields.image === undefined,
                  },
              ]
            : []),
        ...(slideFieldDefinition.contentTabletWidth
            ? [
                  {
                      fieldType: "General",
                      name: fields.contentTabletWidth,
                      displayName: "Tablet Content Width",
                      editorTemplate: "DropDownField",
                      hideEmptyOption: true,
                      defaultValue: "50%",
                      options: [
                          { value: "50%" },
                          { value: "66%" },
                          { value: "75%" },
                          { value: "80%" },
                          { value: "100%" },
                      ],
                      isVisible: (widget: HasFields) => widget.fields.hasContentTabletWidth,
                  },
              ]
            : []),
        ...(slideFieldDefinition.customCSS
            ? [
                  {
                      name: fields.customCSS,
                      displayName: "Custom CSS",
                      editorTemplate: "CodeField",
                      fieldType: "General",
                      tab: advancedTab,
                      defaultValue: defaultCustomCss,
                      isVisible: (widget: HasFields, shouldDisplayAdvancedFeatures: boolean) =>
                          !!shouldDisplayAdvancedFeatures,
                      validate: (value: string) => {
                          if (value === undefined || value === null) {
                              return "";
                          }

                          const result = cssLinter.parse(value, { silent: true });
                          if (result?.stylesheet?.parsingErrors) {
                              // the error output at this time only has room for one line so we just show the first error
                              return result.stylesheet.parsingErrors.length <= 0
                                  ? ""
                                  : result.stylesheet.parsingErrors.map(
                                        error => `${error.reason} on line ${error.line}`,
                                    )[0];
                          }

                          return "Unable to parse Css";
                      },
                      options: {
                          mode: "css",
                          lint: true,
                          autoRefresh: true,
                      },
                  },
              ]
            : []),
    ] as OriginalFieldDefinition[];

const contentFieldDefinitions = getSlideFieldDefinitions({
    customCSS: true,
    clickableSlide: false,
    background: true,
    backgroundImage: true,
    image: true,
    imageLargeResolution: true,
    imageOverlay: true,
    partialOverlay: true,
    partialOverlayPositioning: true,
    backgroundColor: true,
    heading: true,
    subheading: true,
    text: true,
    buttonLabel: true,
    buttonLink: true,
    buttonVariant: true,
    focalPoint: true,
    contentPadding: true,
    headingStyle: true,
    headingColor: true,
    headingUnderline: true,
    subheadingColor: true,
    verticalAlign: true,
    textAlign: true,
    contentSmallDesktopWidth: true,
    contentDesktopWidth: true,
    hasContentMobileWidth: true,
    contentMobileWidth: true,
    hasContentTabletWidth: true,
    contentTabletWidth: true,
    contentPosition: true,
    contentPositionLeftRight: false,
});

const widgetModule: WidgetModule = {
    component: Slide,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: contentFieldDefinitions,
        allowedContexts: [],
    },
};

export default widgetModule;
